import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import UiContext from "@store/ui-context";
import useAPIRequest from "@custom_hooks/simple/useAPIRequest";
import { endOfMonth, format, startOfMonth } from "date-fns";

// one sheet = one month of one department + jenis minyak, so a sheet is never paged
const sheetTake = 500;

const withRelations = {
  with_department: 1,
  with_jenis_minyak: 1,
  with_alat_berat: 1,
  with_operator: 1,
  with_verif_pnj: 1,
  with_permintaan_minyak: 1,
};

export const useAPIGetKartuStokMinyak = () => {
  const uiContext = useContext(UiContext);
  const { requestGet, loading } = useAPIRequest();

  // another page can open a sheet directly (e.g. the permintaan minyak that was received)
  const openedSheet = useLocation().state?.kartuStok;

  const [filterValue, setFilterValue] = useState(() => {
    const tanggal = openedSheet?.tanggal
      ? new Date(String(openedSheet.tanggal).replace(" ", "T"))
      : null;

    return {
      bulan: startOfMonth(tanggal && !isNaN(tanggal) ? tanggal : new Date()),
      department_id: openedSheet?.department_id ?? null,
      data_department: openedSheet?.data_department ?? null,
      jenis_minyak_id: openedSheet?.jenis_minyak_id ?? null,
      data_jenis_minyak: openedSheet?.data_jenis_minyak ?? null,
    };
  });

  const [listData, setListData] = useState([]);

  //  API call section

  const fetchListData = async (customFilterValue) => {
    const fv = customFilterValue ?? filterValue;

    if (!fv.department_id || !fv.jenis_minyak_id || !fv.bulan) {
      setListData([]);
      return;
    }

    const start = startOfMonth(fv.bulan);
    const end = endOfMonth(fv.bulan);
    const sheetFilter = `department_id:=:${fv.department_id};jenis_minyak_id:=:${fv.jenis_minyak_id}`;

    return requestGet({
      fullUrl: `/api/transaksi/kartustokminyak/data`,
      params: {
        page: 1,
        take: sheetTake,
        filter: `${sheetFilter};tanggal_waktu:>=:${format(
          start,
          "yyyy-MM-dd"
        )} 00:00:00;tanggal_waktu:<=:${format(end, "yyyy-MM-dd")} 23:59:59`,
        order: "tanggal_waktu",
        order_method: "ASC",
        ...withRelations,
      },
      onSuccess: ({ data }) => {
        setListData(data.map((el, index) => ({ ...el, no: index + 1 })));
      },
      onError: ({ message }) => uiContext.showErrorMsg("Error", message),
    });
  };

  // other function section

  const sumOf = (field) =>
    listData.reduce((total, row) => total + Number(row[field] ?? 0), 0);

  const totalMasuk = sumOf("masuk");
  const totalKeluar = sumOf("keluar");
  // the balance after the last line of the sheet (null while the sheet is empty)
  const saldoAkhir =
    listData.length > 0 ? Number(listData[listData.length - 1].sisa ?? 0) : null;

  // handle ui event section

  const handleOnClickRefresh = async () => {
    if (!filterValue.department_id || !filterValue.jenis_minyak_id) {
      uiContext.showErrorMsg(
        "Error",
        "Pilih department dan jenis minyak terlebih dahulu"
      );
      return;
    }
    fetchListData();
  };

  // A changed amount moves the balance (sisa) of every line after it: read the sheet again.
  // The line may belong to another sheet than the one that is open (department, jenis minyak
  // or month picked in the dialog): then open that sheet, so the saved line is in view.
  const handleOnUpdateViewRow = (row) => {
    const rowBulan = row?.tanggal_waktu
      ? startOfMonth(new Date(String(row.tanggal_waktu).replace(" ", "T")))
      : null;
    const sameSheet =
      !row?.department_id ||
      !row?.jenis_minyak_id ||
      (row.department_id === filterValue.department_id &&
        row.jenis_minyak_id === filterValue.jenis_minyak_id &&
        (!rowBulan ||
          isNaN(rowBulan) ||
          rowBulan.getTime() === startOfMonth(filterValue.bulan).getTime()));

    if (sameSheet) {
      fetchListData();
      return;
    }

    // the sheet is read again by the effect of the list when the filter changes
    setFilterValue((prevData) => ({
      ...prevData,
      department_id: row.department_id,
      data_department: row.data_department ?? prevData.data_department,
      jenis_minyak_id: row.jenis_minyak_id,
      data_jenis_minyak: row.data_jenis_minyak ?? prevData.data_jenis_minyak,
      bulan: rowBulan && !isNaN(rowBulan) ? rowBulan : prevData.bulan,
    }));
  };

  const handleOnDeleteViewRow = () => {
    fetchListData();
  };

  // only a few fields changed (e.g. the PNJ signature): no need to read the sheet again
  const handleOnPatchViewRow = (row) => {
    setListData((prevList) =>
      prevList.map((el) => (el.id === row.id ? { ...el, ...row } : el))
    );
  };

  return {
    loading,
    listData,
    saldoAkhir,
    totalMasuk,
    totalKeluar,
    fetchListData,
    filterValue,
    setFilterValue,
    handleOnClickRefresh,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
    handleOnPatchViewRow,
  };
};
